import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/es/form/Form";
import { useHistory, useParams } from "react-router-dom";
import { 
  Form, Input, Row, Col, Select, Card, Button, Table, 
  Spin, DatePicker, Tag, Modal, Alert, Statistic, Progress, 
  notification, InputNumber, Typography, Badge, Tooltip, Space, Popconfirm, 
  Tabs,
  Avatar,
  List
} from "antd";
import { 
  LoadingOutlined, DownloadOutlined, LockOutlined, 
  UnlockOutlined, EyeOutlined, ShoppingCartOutlined,
  CheckCircleOutlined, InfoCircleOutlined, CrownOutlined, GiftOutlined,
  HistoryOutlined, CalendarOutlined, DollarOutlined,
  DatabaseOutlined, FileExcelOutlined, ReloadOutlined,
  CreditCardOutlined,
  CloudDownloadOutlined,
  SearchOutlined
} from "@ant-design/icons";
import moment from 'moment';
import { axiosInstance } from "../../util/Api";
import { isArrayNonEmpty, isValidElement } from "../../util/ApiHelper";
import { labelComponent } from "./../../util/UtilComponents";
import { isExporter } from "./../../util/helper";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { createSubscriptionOrder, loadRazorpay, verifyRazorpayPayments } from "../../util/RazorpayHelper";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const Exporterdata = () => {
  let isEditDisabled = false;

  const { id } = useParams();
  const dispatch = useDispatch();
  const [form] = useForm();
  const history = useHistory();
  const { isMobile } = useGetBreakpoints();
  const [hsCodeId, setHsCodeId] = useState(null);
  const [filteredHsCodeData, setFilteredHsCodeData] = useState(null);
  const [hsCodeData, setHsCodeData] = useState(null);
  const authUser = useSelector(({ auth }) => auth.userData);
  const productList = useSelector((state) => state.myProducts.productsList);
  const categoryList = useSelector((state) => state.myProducts.categoryList);
  const hsCodeList = useSelector((state) => state.myProducts.hsCodeList || []);
  const countries = useSelector(({ common }) => common.countries);
  const ports = useSelector(({ common }) => common.ports);

  const [targetedBusinessType, setTargetedBusinessType] = useState(null);
  const [sourcePortsData, setSourcePortsData] = useState(null);
  const [selectedSourceCountry, setSelectedSourceCountry] = useState(
    isExporter(authUser) ? "India" : null
  );
  const [selectedDestinationCountry, setSelectedDestinationCountry] = useState("all");
  const [viewNotification, setViewNotification] = useState(false);
  const [destinationPortsData, setDestinationPortsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); 
  const [indianPorts, setIndianPorts] = useState(ports ?? []);
  const [summaryData, setSummaryData] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [currentDateRange, setCurrentDateRange] = useState([]);
  const [currentFilters, setCurrentFilters] = useState({});
  const [currentFormValues, setCurrentFormValues] = useState({});
  const [accessInfo, setAccessInfo] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userAccessHistory, setUserAccessHistory] = useState([]);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [dailySearchCount, setDailySearchCount] = useState(0);
  const [freeAccessUsed, setFreeAccessUsed] = useState(false);
  const [lifetimeAccess, setLifetimeAccess] = useState([]);
  const [showAccessHistory, setShowAccessHistory] = useState(false);
  const [showPricingPlans, setShowPricingPlans] = useState(false);
  const [showDateInfo, setShowDateInfo] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);

  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failure', null
  const [paymentDetails, setPaymentDetails] = useState(null);

  const roundLoader = <LoadingOutlined style={{ fontSize: 48 }} spin />;

  // Initialize form with default values
  // Initialize form with default values
useEffect(() => {
  form.setFieldsValue({
    sourceCountry: isExporter(authUser) ? "India" : null,
    sourcePort: "all",
    destinationCountry: "all", 
    destinationPort: "all",
    dateRange: [
      moment('2025-10-25', 'YYYY-MM-DD'),
      moment('2025-10-31', 'YYYY-MM-DD')
    ]
  });
  
  setSelectedDestinationCountry("all");
}, [form, authUser]);


  // Fetch initial data
  useEffect(() => {
    if (isArrayNonEmpty(ports)) {
      const filteredIndianPorts = Array.from(
        new Map(
          ports
            .filter((item) => item?.countryId === 105)
            .map((item) => [item.port, item])
        ).values()
      );
      setIndianPorts(filteredIndianPorts);
    }
  }, [ports]);

  useEffect(() => {
  if (hsCodeList?.length) {
    // Filter to show only HS codes starting with 1006
    const filteredData = hsCodeList.filter(item => {
      const codeStr = item.code.toString();
      return codeStr.startsWith("1006");
    });
    
    setHsCodeData(filteredData);
    setFilteredHsCodeData(null);
  }
}, [hsCodeList]);

  useEffect(() => {
    const countryId = countries.find(
      (item) => item.country === selectedSourceCountry
    )?.id;
    setSourcePortsData(ports.filter((item) => item.countryId === countryId));
  }, [countries, ports, selectedSourceCountry]);

  useEffect(() => {
    if (selectedDestinationCountry && isArrayNonEmpty(countries) && isArrayNonEmpty(ports)) {
      const cleanSelectedCountry = selectedDestinationCountry.trim().toUpperCase();
      
      const filteredPorts = ports.filter(item => {
        if (!item.countryName) return false;
        const portCountry = item.countryName.trim().toUpperCase();
        return portCountry === cleanSelectedCountry;
      });
      
      setDestinationPortsData(filteredPorts);
    } else {
      setDestinationPortsData([]);
    }
  }, [selectedDestinationCountry, countries, ports]);

  // Fetch user data
  useEffect(() => {
    if (authUser?.id) {
      fetchAllUserData();
    }
  }, [authUser]);

  const fetchAllUserData = async () => {
  try {
    const [plansResponse, historyResponse, freeAccessResponse, lifetimeResponse] = await Promise.all([
      axiosInstance.get('/exim-data/pricing-plans'),
      axiosInstance.get('/exim-data/user-access-history'),
      axiosInstance.get('/exim-data/check-free-access'),
      axiosInstance.get('/exim-data/lifetime-access')
    ]);

    const accessHistory = historyResponse.data.data || [];


    const downloads = accessHistory.map(item => ({
      id: item.id,
      title: `${item.hsCode || 'All HS Codes'} Data`,
      description: `From ${item.sourceCountry || 'India'} to ${item.destinationCountry || 'All Countries'}`,
      hsCode: item.hsCode,
      sourceCountry: item.sourceCountry,
      destinationCountry: item.destinationCountry,
      sourcePort: item.sourcePort,
      destinationPort: item.destinationPort,
      dateRangeStart: item.dateRangeStart,
      dateRangeEnd: item.dateRangeEnd,
      unlockedAt: item.unlockedAt,
      accessType: item.accessType || (item.price === 0 ? 'free' : 'paid'),
      price: item.price || 0,
      filters: {
        hscode: item.hsCode,
        sourceCountry: item.sourceCountry,
        sourcePort: item.sourcePort,
        destinationCountry: item.destinationCountry,
        destinationPort: item.destinationPort,
        dateFrom: item.dateRangeStart,
        dateTo: item.dateRangeEnd,
      }
    }));
    
    setMyDownloads(downloads);
    
    setPricingPlans(plansResponse.data.data || []);
    setUserAccessHistory(historyResponse.data.data || []);
    setFreeAccessUsed(freeAccessResponse.data.freeAccessUsed || false);
    setLifetimeAccess(lifetimeResponse.data.data || []);
  } catch (error) {
    console.error('Error fetching user data:', error);
  }
};

// Add this useEffect after your other useEffects
useEffect(() => {
  // Close payment modal when payment status changes (success or failure)
  if (paymentStatus === 'success' || paymentStatus === 'failure') {
    setShowPaymentModal(false);
  }
}, [paymentStatus]);

 

  const handleHSCodeSearch = (value) => {
  const trimmedValue = value.trim();
  
  if (!trimmedValue) {
    // If search is empty, clear filtered data (will show all)
    setFilteredHsCodeData(null);
    return;
  }

  const containsOnlyNumbers = /^\d+$/.test(trimmedValue);
  const searchTerm = trimmedValue.toLowerCase();

  setFilteredHsCodeData(() => {
    if (!isValidElement(hsCodeData)) return [];
    
    return hsCodeData.filter((option) => {
      const codeString = option.code.toString().toLowerCase();
      const descriptionString = option.description
        .replace(/[\n\r"]/g, "")
        .toLowerCase();

      if (containsOnlyNumbers) {
        // For number searches: Show codes starting with the number (e.g., "1006" for "10063012")
        // Match by first 4 digits when searching with 4+ digits
        if (searchTerm.length >= 4) {
          // Match exact first 4 digits
          return codeString.startsWith(searchTerm.substring(0, 4));
        } else {
          // For shorter numbers, show any containing the number
          return codeString.includes(searchTerm);
        }
      } else {
        // For text searches: Show descriptions containing the text
        return descriptionString.includes(searchTerm);
      }
    });
  });
};

<Select
  showSearch
  size="large"
  name="hsCode"
  placeholder="Search HS code (e.g., 1006) or Item Description"
  filterOption={false}
  dropdownStyle={{ maxHeight: "200px", overflowY: "auto" }}
  getPopupContainer={(trigger) => trigger.parentNode}
  disabled={isEditDisabled}
  onChange={(value) => {
    setHsCodeId(value);
  }}
  onSearch={handleHSCodeSearch}
  notFoundContent={
    <div style={{ textAlign: 'center', padding: '10px' }}>
      <p>No HS Code found</p>
      <small style={{ color: '#999' }}>
        Try searching by number (e.g., 1006) or item description
      </small>
    </div>
  }
>
  {isValidElement(filteredHsCodeData)
    ? filteredHsCodeData?.map((option) => (
        <Option key={option.id} value={option.id}>
          <div style={{ whiteSpace: "normal", fontFamily: filteredHsCodeData.length > 0 ? 'monospace' : 'inherit' }}>
            <strong>{option.code}</strong> -{" "}
            {option.description.replace(/[\n\r"]/g, "")}
          </div>
        </Option>
      ))
    : hsCodeData?.map((option) => (
        <Option key={option.id} value={option.id}>
          <div style={{ whiteSpace: "normal" }}>
            <strong>{option.code}</strong> -{" "}
            {option.description.replace(/[\n\r"]/g, "")}
          </div>
        </Option>
      ))}
</Select>

  

  const handleSearch = async (values) => {
    try {
      setLoading(true);
      setSummaryLoading(true);
      
      const selectedHsCode = hsCodeData.find(hsCode => hsCode.id === values.hsCode);
      
      let dateFrom = null;
      let dateTo = null;
      if (values.dateRange && values.dateRange.length === 2) {
        dateFrom = values.dateRange[0].format('YYYY-MM-DD');
        dateTo = values.dateRange[1].format('YYYY-MM-DD');
        setCurrentDateRange([dateFrom, dateTo]);
      }

      const filters = {
        hscode: selectedHsCode ? selectedHsCode.code : null, 
        sourceCountry: values.sourceCountry || null,
        sourcePort: values.sourcePort === 'all' ? 'all' : values.sourcePort || null,
        destinationCountry: values.destinationCountry === 'all' ? 'all' : values.destinationCountry || null,
        destinationPort: values.destinationPort === 'all' ? 'all' : values.destinationPort || null,
        dateFrom: dateFrom,
        dateTo: dateTo,
      };
      
      console.log('Filters being sent:', filters);
      
      setCurrentFilters(filters);
      setCurrentFormValues(values);
      
      // Check if it's free access and already used
      const daysSelected = moment(dateTo).diff(moment(dateFrom), 'days');
      
      if (daysSelected <= 7 && freeAccessUsed) {
        setAccessInfo({
          hasAccess: false,
          isFreeAccess: true,
          remainingSearches: 0,
          priceAmount: null,
          message: 'You have already used your lifetime free access (7 days). Please upgrade to paid plan.',
          requiredPlan: pricingPlans.find(p => p.price > 0) || null
        });
        setSummaryData([]);
        setHasSearched(true);
        setSummaryLoading(false);
        setLoading(false);
        
        notification.warning({
          message: 'Free Access Already Used',
          description: 'You have already used your lifetime free access. Please select a paid plan.',
          duration: 5,
        });
        return;
      }
      
      // Fetch summary data
      await fetchSummaryData(filters, values);
      
    } catch (error) {
      console.error('Error in search:', error);
      notification.error({
        message: 'Search Error',
        description: 'Failed to process search request',
      });
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  };



  const PriceDisplay = () => {
  if (!priceInfo && !accessInfo) return null;
  
  const days = priceInfo?.days || Math.ceil(
    moment(currentDateRange[1]).diff(moment(currentDateRange[0]), 'days')
  );
  const price = priceInfo?.price || accessInfo?.priceAmount || 0;
  const plan = priceInfo?.plan || accessInfo?.requiredPlan;
  const isFreeEligible = priceInfo?.isFreeEligible || (days <= 7);
  const freeAccessUsed = priceInfo?.freeAccessUsed || freeAccessUsed;
  
  return (
    <Card size="small" style={{ 
      marginTop: 16, 
      marginBottom: 16,
      border: '1px solid #d9d9d9',
      backgroundColor: '#fafafa'
    }}>
      <Row gutter={16} align="middle">
        <Col xs={24} sm={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarOutlined style={{ color: '#1890ff' }} />
            <div>
              <Text strong>Date Range</Text>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {days} days
              </div>
            </div>
          </div>
        </Col>
        
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center' }}>
            {accessInfo?.hasAccess ? (
  <Alert
    message="Access Granted"
    description={accessInfo.isFreeAccess ? "Free lifetime access" : "Paid lifetime access"}
    type="success"
    showIcon
    size="small"
  />
) : (
  <Button
    type="primary"
    icon={<ShoppingCartOutlined />}
    onClick={() => {
      if (accessInfo?.requiredPlan) {
        setSelectedPlan(accessInfo.requiredPlan);
      } else if (plan) {
        setSelectedPlan(plan);
      } else {
        // Find appropriate plan based on days
        const requiredPlan = pricingPlans.find(p => 
          p.period_days >= days && p.price > 0
        ) || pricingPlans.find(p => p.price > 0);
        setSelectedPlan(requiredPlan);
      }
      setShowPaymentModal(true);
    }}
    style={{ width: '100%' }}
  >
    {price > 0 ? `Purchase for ₹${price.toLocaleString('en-IN')}` : 'Unlock Data'}
  </Button>
)}
          </div>
        </Col>
        
        <Col xs={24} sm={8}>
          {accessInfo?.hasAccess ? (
            <Alert
              message="Access Granted"
              description={accessInfo.isFreeAccess ? "Free lifetime access" : "Paid lifetime access"}
              type="success"
              showIcon
              size="small"
            />
          ) : (
            <Button
              type="primary"
              icon={<ShoppingCartOutlined />}
              onClick={() => {
                if (accessInfo?.requiredPlan) {
                  setSelectedPlan(accessInfo.requiredPlan);
                } else if (plan) {
                  setSelectedPlan(plan);
                } else {
                  // Find appropriate plan based on days
                  const requiredPlan = pricingPlans.find(p => 
                    p.period_days >= days && p.price > 0
                  ) || pricingPlans.find(p => p.price > 0);
                  setSelectedPlan(requiredPlan);
                }
                setShowPaymentModal(true);
              }}
              style={{ width: '100%' }}
            >
              {price > 0 ? `Purchase for ₹${price.toLocaleString('en-IN')}` : 'Unlock Data'}
            </Button>
          )}
        </Col>
      </Row>
      
      {isFreeEligible && freeAccessUsed && (
        <Alert
          message="Free Access Already Used"
          description="You have already used your one-time free access. This data now requires payment."
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
        />
      )}
    </Card>
  );
};

  // In your React component, update the fetchSummaryData function:
const fetchSummaryData = async (filters, formValues) => {
  try {
    setSummaryLoading(true);
    const response = await axiosInstance.get('/exim-data/summary', {
      params: filters,
    });
    
    // Handle payment required case
    if (response.status === 402) {
      setAccessInfo(response.data.accessInfo);
      setPriceInfo(response.data.priceInfo); // Store price info
      setSummaryData([]);
      
      if (response.data.accessInfo.requiredPlan) {
        setSelectedPlan(response.data.accessInfo.requiredPlan);
      }
      
      notification.warning({
        message: 'Access Restricted',
        description: response.data.message,
        duration: 5,
      });
      return;
    }
    
    let processedData = response.data.data || [];
    let totalRecordCount = 0;

    processedData.forEach(item => {
      totalRecordCount += item.recordCount;
    });

    const hasAllSelection = 
      formValues.sourcePort === 'all' || 
      formValues.destinationPort === 'all' || 
      formValues.destinationCountry === 'all';

    if (hasAllSelection) {
      const consolidatedRow = {
        hsCode: filters.hscode || 'N/A',
        loadingPort: formValues.sourcePort === 'all' ? 'All Ports' : (processedData[0]?.loadingPort || 'Specific Port'),
        destinationPort: formValues.destinationPort === 'all' ? 'All Ports' : (processedData[0]?.destinationPort || 'Specific Port'),
        destinationCountry: formValues.destinationCountry === 'all' ? 'All Countries' : (processedData[0]?.destinationCountry || 'Specific Country'),
        recordCount: totalRecordCount,
        dateRange: filters.dateFrom && filters.dateTo 
          ? `${filters.dateFrom} to ${filters.dateTo}`
          : 'N/A',
        isConsolidated: true,
        formValues: formValues,
        accessInfo: response.data.accessInfo
      };
      
      processedData = [consolidatedRow];
    } else {
      processedData = processedData.map(item => ({
        ...item,
        dateRange: filters.dateFrom && filters.dateTo 
          ? `${filters.dateFrom} to ${filters.dateTo}`
          : 'N/A',
        isConsolidated: false,
        formValues: formValues,
        accessInfo: response.data.accessInfo
      }));
    }
    
    setSummaryData(processedData);
    setAccessInfo(response.data.accessInfo);
    setPriceInfo(response.data.priceInfo); // Store price info from API
    
    setHasSearched(true);
    
  } catch (error) {
    console.error('Error fetching summary data:', error);
    if (error.response?.status === 402) {
      setAccessInfo(error.response.data.accessInfo);
      setPriceInfo(error.response.data.priceInfo); // Store price info
      setSummaryData([]);
      
      if (error.response.data.accessInfo.requiredPlan) {
        setSelectedPlan(error.response.data.accessInfo.requiredPlan);
      }
      
      notification.warning({
        message: 'Access Restricted',
        description: error.response.data.message,
        duration: 5,
      });
    } else {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch summary data',
      });
    }
  } finally {
    setSummaryLoading(false);
  }
};

  const handleDownloadSpecific = async (summaryItem) => {
    try {
      if (!summaryItem.accessInfo?.hasAccess) {
        notification.warning({
          message: 'Access Required',
          description: 'You need to unlock this data before downloading',
        });
        return;
      }
      
      setDownloadingExcel(true);
      
      const formValues = summaryItem.formValues || form.getFieldsValue();
      const selectedHsCode = hsCodeData.find(hsCode => hsCode.id === formValues.hsCode);
      
      let dateFrom = null;
      let dateTo = null;
      if (formValues.dateRange && formValues.dateRange.length === 2) {
        dateFrom = formValues.dateRange[0].format('YYYY-MM-DD');
        dateTo = formValues.dateRange[1].format('YYYY-MM-DD');
      }

      const filters = {
        hscode: selectedHsCode ? selectedHsCode.code : null, 
        sourceCountry: formValues.sourceCountry === 'all' ? null : formValues.sourceCountry,
        destinationCountry: summaryItem.isConsolidated ? (formValues.destinationCountry === 'all' ? null : formValues.destinationCountry) : summaryItem.destinationCountry,
        sourcePort: summaryItem.isConsolidated ? (formValues.sourcePort === 'all' ? null : formValues.sourcePort) : summaryItem.loadingPort,
        destinationPort: summaryItem.isConsolidated ? (formValues.destinationPort === 'all' ? null : formValues.destinationPort) : summaryItem.destinationPort,
        dateFrom: dateFrom,
        dateTo: dateTo,
      };
      
      const response = await axiosInstance.post(
        '/exim-data/download-excel',
        { filters },
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Exim_data_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      notification.success({
        message: 'Download Started',
        description: `Excel file download has started`,
      });
      
    } catch (error) {
      console.error('Error downloading specific data:', error);
      if (error.response?.status === 402) {
        notification.warning({
          message: 'Access Required',
          description: error.response.data.message,
        });
      } else {
        notification.error({
          message: 'Download Failed',
          description: 'Failed to download data. Please try again.',
        });
      }
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleUnlockData = async (plan) => {
  try {
    // Set loading state IMMEDIATELY
    setPurchaseLoading(true);
    
    const formValues = form.getFieldsValue();
    const selectedHsCode = hsCodeData.find(hsCode => hsCode.id === formValues.hsCode);
    
    // Check if it's free plan
    if (plan.price === 0) {
      await handleFreeAccess();
      setPurchaseLoading(false);
      setShowPaymentModal(false);
      return;
    }
    
    // Prepare purchase data for paid plan
    const purchaseData = {
      hscode: selectedHsCode ? selectedHsCode.code : null,
      sourceCountry: formValues.sourceCountry,
      sourcePort: formValues.sourcePort,
      destinationCountry: formValues.destinationCountry,
      destinationPort: formValues.destinationPort,
      dateFrom: formValues.dateRange[0].format('YYYY-MM-DD'),
      dateTo: formValues.dateRange[1].format('YYYY-MM-DD'),
      planId: plan.id,
      planLabel: plan.period_label
    };
    
    // Create Razorpay order
    const orderResponse = await axiosInstance.post('/exim-data/create-razorpay-order', {
      planId: plan.id,
      amount: plan.price,
      eximData: purchaseData
    });
    
    if (!orderResponse.data.success) {
      throw new Error(orderResponse.data.message);
    }
    
    // Load Razorpay SDK (with loading state)
    try {
      await loadRazorpay();
    } catch (error) {
      throw new Error('Payment system not available. Please try again.');
    }
    
    if (!window.Razorpay) {
      throw new Error('Payment system not available');
    }
    
    // Store purchase details for success/failure modals
    const purchaseDetails = {
      plan: plan,
      amount: plan.price,
      orderId: orderResponse.data.order.id,
      dateRange: `${purchaseData.dateFrom} to ${purchaseData.dateTo}`,
      hsCode: purchaseData.hscode
    };
    
    // Razorpay checkout options
    const checkoutOptions = {
      key: orderResponse.data.razorpayKey || process.env.REACT_APP_RZP_KEY_ID,
      amount: orderResponse.data.order.amount,
      currency: orderResponse.data.order.currency,
      name: "MY EXIM BUSINESS",
      image: "https://myeximbusiness.com/favicon.ico",
      description: `Exim Data Access - ${plan.period_label}`,
      order_id: orderResponse.data.order.id,
      handler: async (response) => {
        try {
          // Show processing state
          setPurchaseLoading(true);
          
          // Complete the purchase
          const completeResponse = await axiosInstance.post('/exim-data/complete-purchase', {
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
            purchaseData: purchaseData
          });
          
          if (completeResponse.data.success) {
            // Set success state
            setPaymentStatus('success');
            setPaymentDetails({
              ...purchaseDetails,
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              transactionTime: new Date().toISOString()
            });
            
            // Refresh user data
            await fetchAllUserData();
            
            // Refresh the current search
            if (hasSearched) {
              await handleSearch(formValues);
            }
            
          } else {
            throw new Error(completeResponse.data.message);
          }
          
        } catch (error) {
          console.error('Payment completion error:', error);
          // Set failure state
          setPaymentStatus('failure');
          setPaymentDetails({
            ...purchaseDetails,
            error: error.message || 'Failed to complete payment'
          });
          
          notification.error({
            message: 'Payment Completion Failed',
            description: error.response?.data?.message || 'Failed to complete payment. Please contact support.',
          });
        } finally {
          // Reset loading state AFTER payment is completed (success or failure)
          setPurchaseLoading(false);
        }
      },
      prefill: {
        name: authUser.name,
        email: authUser.email,
        contact: authUser.mobile || ''
      },
      notes: {
        purchaseType: 'exim_data',
        planId: plan.id,
        userId: authUser.id,
        userUuid: authUser.uuid,
        paymentHistoryId: orderResponse.data.paymentHistoryId
      },
      theme: {
        color: '#722ed1'
      },
      modal: {
        ondismiss: () => {
          console.log('Payment modal dismissed');
          // Reset loading state when user closes the Razorpay modal
          setPurchaseLoading(false);
          setSelectedPlan(null);
        }
      }
    };
    
    const rzp = new window.Razorpay(checkoutOptions);
    
    rzp.on('payment.failed', (response) => {
      console.error('Payment failed:', response.error);
      
      // Set failure state
      setPaymentStatus('failure');
      setPaymentDetails({
        ...purchaseDetails,
        error: response.error.description || 'Payment could not be completed',
        paymentId: response.error.metadata?.payment_id,
        orderId: response.error.metadata?.order_id
      });
      
      // Log payment failure on backend
      axiosInstance.post('/exim-data/payment-failed', {
        razorpayOrderId: response.error.metadata?.order_id,
        errorDescription: response.error.description
      }).catch(err => console.error('Failed to log payment failure:', err));
      
      notification.error({
        message: 'Payment Failed',
        description: response.error.description || 'Payment could not be completed',
      });
      
      // Reset loading state
      setPurchaseLoading(false);
    });
    
    rzp.on('close', () => {
      console.log('Razorpay payment interface closed');
      // Reset loading state when Razorpay modal closes
      setPurchaseLoading(false);
      setSelectedPlan(null);
    });
    
    // Open Razorpay - loading will continue to show until Razorpay opens
    rzp.open();
    
  } catch (error) {
    console.error('Error initiating payment:', error);
    
    // Set failure state
    setPaymentStatus('failure');
    setPaymentDetails({
      error: error.message || 'Failed to initiate payment',
      plan: plan
    });
    
    notification.error({
      message: 'Payment Error',
      description: error.message || 'Failed to initiate payment. Please try again.',
    });
    
    // Reset loading state on error
    setPurchaseLoading(false);
  }
};



const handleFreeAccess = async () => {
  try {
    setLoading(true); // Use existing loading state or create a new one
    
    const formValues = form.getFieldsValue();
    const selectedHsCode = hsCodeData.find(hsCode => hsCode.id === formValues.hsCode);
    
    // Calculate days to ensure it's 7 days or less
    const daysSelected = Math.ceil(
      moment(formValues.dateRange[1]).diff(
        moment(formValues.dateRange[0]), 'days'
      )
    );
    
    if (daysSelected > 7) {
      notification.error({
        message: 'Free Access Not Available',
        description: 'Free access is only available for 7 days or less. Please select a date range of 7 days or less.',
      });
      setLoading(false);
      return;
    }
    
    const freeAccessData = {
      hscode: selectedHsCode ? selectedHsCode.code : null,
      sourceCountry: formValues.sourceCountry,
      sourcePort: formValues.sourcePort,
      destinationCountry: formValues.destinationCountry,
      destinationPort: formValues.destinationPort,
      dateFrom: formValues.dateRange[0].format('YYYY-MM-DD'),
      dateTo: formValues.dateRange[1].format('YYYY-MM-DD')
    };
    
    const response = await axiosInstance.post('/exim-data/activate-free-access', freeAccessData);
    
    if (response.data.success) {
      notification.success({
        message: 'Free Access Activated!',
        description: `You now have free lifetime access to data from ${freeAccessData.dateFrom} to ${freeAccessData.dateTo}. This free access can only be used once per account.`,
        duration: 5,
      });
      
      // Refresh all user data
      await fetchAllUserData();
      
      // Refresh the current search to show unlocked data
      if (hasSearched) {
        await handleSearch(formValues);
      }
      
      // Show success details
      Modal.success({
        title: 'Free Access Activated!',
        content: (
          <div>
            <p><strong>Date Range:</strong> {freeAccessData.dateFrom} to {freeAccessData.dateFrom}</p>
            <p><strong>Access Type:</strong> Free Lifetime Access</p>
            <p><strong>Note:</strong> This free access is valid only for this specific date range. For other date ranges, you will need to purchase access.</p>
          </div>
        ),
        okText: 'Continue',
      });
      
      // Close the modal
      setShowPaymentModal(false);
      setSelectedPlan(null);
    } else {
      throw new Error(response.data.message);
    }
    
  } catch (error) {
    console.error('Error activating free access:', error);
    notification.error({
      message: 'Free Access Error',
      description: error.response?.data?.message || 'Failed to activate free access. You may have already used your free access.',
    });
  } finally {
    setLoading(false);
    setPurchaseLoading(false);
  }
};

  const AccessStatus = () => {
  if (!accessInfo && !hasSearched) return null;

  // New unified variables
  const hasAccess = accessInfo?.hasAccess;
  const isFreeAccess = accessInfo?.isFreeAccess;
  const priceAmount = accessInfo?.priceAmount || accessInfo?.requiredPlan?.price;

  // New feature: Check if user used free access previously but for another date
  const hasDifferentFreeAccess = priceInfo?.freeAccessForDifferentRange;

  // Old feature: Lifetime access check
  const hasLifetimeAccess =
    accessInfo?.accessRecord?.is_lifetime ||
    lifetimeAccess.some(access =>
      access.hsCode === (currentFilters.hscode || 'all') &&
      new Date(access.dateRangeStart) <= new Date(currentFilters.dateFrom) &&
      new Date(access.dateRangeEnd) >= new Date(currentFilters.dateTo)
    );

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderLeft: `4px solid ${
          hasLifetimeAccess
            ? '#722ed1'
            : hasAccess
            ? isFreeAccess
              ? '#52c41a'
              : '#722ed1'
            : '#ff4d4f'
        }`,
        backgroundColor: !hasAccess && priceAmount ? '#fff7e6' : 'white',
      }}
    >
      <Row gutter={16} align="middle">
        {/* LEFT BLOCK */}
        <Col xs={24} sm={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {hasAccess ? (
              <>
                <CheckCircleOutlined
                  style={{
                    color: isFreeAccess ? '#52c41a' : '#722ed1',
                    fontSize: '20px',
                  }}
                />
                <span
                  style={{
                    color: isFreeAccess ? '#52c41a' : '#722ed1',
                    fontWeight: 'bold',
                  }}
                >
                  {hasLifetimeAccess
                    ? 'Lifetime Access'
                    : isFreeAccess
                    ? 'Free Access'
                    : 'Paid Access'}
                </span>
              </>
            ) : (
              <>
                <LockOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  Access Required
                </span>
              </>
            )}

            <Tag color={isFreeAccess ? 'green' : hasAccess ? 'blue' : 'red'}>
              {isFreeAccess ? 'Free' : hasAccess ? 'Paid' : 'Locked'}
            </Tag>
          </div>

          <Text
            type="secondary"
            style={{ fontSize: '12px', marginTop: 4, display: 'block' }}
          >
            {hasDifferentFreeAccess
              ? 'You already used free access for a different date range'
              : accessInfo?.message || 'Search to check access'}
          </Text>
        </Col>

        {/* MIDDLE BLOCK */}
        <Col xs={24} sm={8}>
          {!hasAccess && priceAmount ? (
            <Alert
              message={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CreditCardOutlined style={{ color: '#fa8c16' }} />
                  <span>
                    Unlock for{' '}
                    <Text
                      strong
                      style={{ color: '#fa8c16', fontSize: '16px' }}
                    >
                      ₹{priceAmount.toLocaleString()}
                    </Text>
                  </span>
                </div>
              }
              type="warning"
              showIcon={false}
              style={{ border: '1px solid #faad14' }}
            />
          ) : hasAccess ? (
            <Alert
              message={
                hasLifetimeAccess
                  ? 'Unlimited Lifetime Access'
                  : isFreeAccess
                  ? 'Free Access Activated'
                  : 'Paid Access Active'
              }
              description={
                hasLifetimeAccess
                  ? 'Lifetime unlimited access'
                  : isFreeAccess
                  ? '7 days free access'
                  : 'Unlimited access during plan period'
              }
              type="success"
              showIcon
              style={{ fontSize: '12px' }}
            />
          ) : (
            <Alert
              message="No Access"
              description="Purchase a plan to unlock this data"
              type="info"
              showIcon
              style={{ fontSize: '12px' }}
            />
          )}
        </Col>

        {/* RIGHT BLOCK */}
        <Col xs={24} sm={8}>
          {!hasAccess ? (
           <Button
  type="primary"
  size="small"
  icon={<ShoppingCartOutlined />}
  onClick={() => {
    if (accessInfo?.requiredPlan) {
      setSelectedPlan(accessInfo.requiredPlan);
      setShowPaymentModal(true);
    } else {
      setShowPricingPlans(true);
    }
  }}
  loading={purchaseLoading}
  disabled={purchaseLoading} // Disable button when loading
  style={{ width: '100%' }}
>
  {purchaseLoading ? 'Processing...' : (priceAmount ? `Purchase for ₹${priceAmount}` : 'View Plans')}
</Button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {accessInfo.accessRecord?.price && (
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Purchased for
                  </Text>
                  <Text
                    strong
                    style={{ color: '#722ed1', fontSize: '16px' }}
                  >
                    ₹{accessInfo.accessRecord.price.toLocaleString()}
                  </Text>
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};




const MyDownloadsTable = () => {
  const columns = [
    {
      title: 'HS Code',
      dataIndex: 'hsCode',
      key: 'hsCode',
      width: 100,
      render: (text) => <Text strong>{text || 'All'}</Text>
    },
    {
      title: 'Source',
      key: 'source',
      width: 150,
      render: (record) => (
        <div>
          <div><Text type="secondary">Country:</Text> {record.sourceCountry || 'India'}</div>
          <div><Text type="secondary">Port:</Text> {record.sourcePort || 'All'}</div>
        </div>
      )
    },
    {
      title: 'Destination',
      key: 'destination',
      width: 150,
      render: (record) => (
        <div>
          <div><Text type="secondary">Country:</Text> {record.destinationCountry || 'All'}</div>
          <div><Text type="secondary">Port:</Text> {record.destinationPort || 'All'}</div>
        </div>
      )
    },
    {
      title: 'Date Range',
      key: 'dateRange',
      width: 120,
      render: (record) => (
        <div style={{ fontSize: '12px' }}>
          {moment(record.dateRangeStart).format('MMM D, YY')}
          <br />
          to
          <br />
          {moment(record.dateRangeEnd).format('MMM D, YY')}
        </div>
      )
    },
    {
      title: 'Access Type',
      dataIndex: 'accessType',
      key: 'accessType',
      width: 100,
      render: (type) => (
        <Tag 
          color={type === 'free' ? 'green' : 'purple'}
          icon={type === 'free' ? <GiftOutlined /> : <CrownOutlined />}
        >
          {type === 'free' ? 'FREE' : 'PAID'}
        </Tag>
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price) => price ? (
        <Text strong>₹{price.toLocaleString()}</Text>
      ) : (
        <Text type="secondary">FREE</Text>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (record) => (
        <Space>
          <Tooltip title="Download data">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownloadFromMyDownloads(record)}
              loading={downloadingExcel}
            >
              Download
            </Button>
          </Tooltip>
          
        </Space>
      )
    }
  ];



  if (myDownloads.length === 0) {
    return (
      <Card style={{ marginTop: 24 }}>
        <div className="myexim-table-title" style={{ marginBottom: 16 }}>
          {"My Downloads"}
          <Badge 
            count={0} 
            style={{ 
              backgroundColor: '#d9d9d9',
              marginLeft: 8
            }} 
          />
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <CloudDownloadOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Title level={4} style={{ color: '#888' }}>No Downloads Yet</Title>
          <p style={{ color: '#888', marginBottom: '24px' }}>
            Your accessed data will appear here after you unlock or purchase data
          </p>
          <Button 
            type="primary" 
            onClick={() => {
              // Focus on search form
              const searchForm = document.querySelector('.myexim-detail-card');
              if (searchForm) {
                searchForm.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Start Searching
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: 24 }}>
      <div className="myexim-table-title" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {"My Downloads"}
          <Badge 
            count={myDownloads.length} 
            style={{ 
              backgroundColor: '#52c41a',
              marginLeft: 8
            }} 
          />
        </div>
        {/* <Button
          type="link"
          icon={<ReloadOutlined />}
          onClick={fetchMyDownloads}
          loading={downloadsLoading}
        >
          Refresh
        </Button> */}
      </div>
      
      {isMobile ? (
        // Mobile view for My Downloads
        <List
          dataSource={myDownloads}
          loading={downloadsLoading}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="download"
                  type="primary"
                  icon={<DownloadOutlined />}
                  size="small"
                  onClick={() => handleDownloadFromMyDownloads(item)}
                  loading={downloadingExcel}
                >
                  Download
                </Button>,
                
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar 
                    icon={item.accessType === 'free' ? <GiftOutlined /> : <CrownOutlined />} 
                    style={{ 
                      backgroundColor: item.accessType === 'free' ? '#52c41a' : '#722ed1' 
                    }}
                  />
                }
                title={
                  <div>
                    <Text strong>HS Code: {item.hsCode || 'All'}</Text>
                    {item.accessType === 'free' ? (
                      <Tag color="green" style={{ marginLeft: 8, fontSize: '10px' }}>FREE</Tag>
                    ) : (
                      <Tag color="purple" style={{ marginLeft: 8, fontSize: '10px' }}>
                        ₹{item.price?.toLocaleString()}
                      </Tag>
                    )}
                  </div>
                }
                description={
                  <div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      From: {item.sourceCountry || 'India'} ({item.sourcePort || 'All'})
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      To: {item.destinationCountry || 'All'} ({item.destinationPort || 'All'})
                    </div>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: 4 }}>
                      {moment(item.dateRangeStart).format('MMM D, YY')} - {moment(item.dateRangeEnd).format('MMM D, YY')}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        // Desktop table view
        <Table
          dataSource={myDownloads}
          columns={columns}
          loading={downloadsLoading}
          size="middle"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} datasets`,
            showQuickJumper: true,
          }}
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />
      )}
    </Card>
  );
};



  const PricingCard = ({ plan, isSelected = false, onSelect }) => (
    <Card 
      style={{ 
        textAlign: 'center', 
        border: `2px solid ${isSelected ? '#1890ff' : (plan.price === 0 ? '#52c41a' : '#722ed1')}`,
        borderRadius: '12px',
        height: '100%',
        transition: 'all 0.3s',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#f0f9ff' : 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
      className="pricing-card"
      hoverable
      onClick={() => onSelect(plan)}
    >
      {plan.price === 0 ? (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '-30px',
          backgroundColor: '#52c41a',
          color: 'white',
          padding: '4px 30px',
          transform: 'rotate(45deg)',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          FREE
        </div>
      ) : (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '-30px',
          backgroundColor: '#1890ff',
          color: 'white',
          padding: '4px 30px',
          transform: 'rotate(45deg)',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          PREMIUM
        </div>
      )}
      
      <div style={{ 
        fontSize: '24px', 
        color: plan.price === 0 ? '#52c41a' : '#722ed1', 
        marginBottom: '8px' 
      }}>
        {plan.price === 0 ? <GiftOutlined /> : <CrownOutlined />}
      </div>
      
      <h3 style={{ margin: '8px 0', color: plan.price === 0 ? '#52c41a' : '#722ed1' }}>
        {plan.period_label}
      </h3>
      <div style={{ 
        fontSize: '28px', 
        fontWeight: 'bold', 
        color: plan.price === 0 ? '#52c41a' : '#722ed1', 
        margin: '16px 0' 
      }}>
        {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString()}`}
      </div>
      <p style={{ color: '#666', marginBottom: '20px', minHeight: '40px' }}>
        {plan.description || `${plan.period_days} days data access`}
      </p>
      
      <div style={{ textAlign: 'left', marginBottom: '20px', padding: '0 16px' }}>
        <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Features:</h4>
        {plan.features && Array.isArray(plan.features) ? (
          plan.features.map((feature, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
              <CheckCircleOutlined style={{ color: plan.price === 0 ? '#52c41a' : '#722ed1', marginRight: '8px', marginTop: '2px' }} />
              <span style={{ fontSize: '12px' }}>{feature}</span>
            </div>
          ))
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
              <CheckCircleOutlined style={{ color: plan.price === 0 ? '#52c41a' : '#722ed1', marginRight: '8px' }} />
              <span style={{ fontSize: '12px' }}>Lifetime access to data range</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
              <CheckCircleOutlined style={{ color: plan.price === 0 ? '#52c41a' : '#722ed1', marginRight: '8px' }} />
              <span style={{ fontSize: '12px' }}>Unlimited searches & downloads</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
              <CheckCircleOutlined style={{ color: plan.price === 0 ? '#52c41a' : '#722ed1', marginRight: '8px' }} />
              <span style={{ fontSize: '12px' }}>Excel export included</span>
            </div>
          </>
        )}
      </div>
      
      <Button
        type={isSelected ? "primary" : plan.price === 0 ? "default" : "primary"}
        size="large"
        icon={plan.price === 0 ? <GiftOutlined /> : <UnlockOutlined />}
        style={{ 
          width: '100%',
          backgroundColor: plan.price === 0 ? '#f6ffed' : isSelected ? '#1890ff' : '#722ed1',
          borderColor: plan.price === 0 ? '#b7eb8f' : isSelected ? '#1890ff' : '#722ed1',
          color: plan.price === 0 ? '#52c41a' : 'white'
        }}
      >
        {plan.price === 0 ? 'Use Free Access' : 'Purchase Now'}
      </Button>
    </Card>
  );

 const PaymentModal = () => (
  <Modal
    title="Unlock Data Access"
    visible={showPaymentModal}
    onCancel={() => {
      if (!purchaseLoading) {
        setShowPaymentModal(false);
        setSelectedPlan(null);
      }
    }}
    footer={null}
    width={800}
    destroyOnClose
    closable={!purchaseLoading}
    maskClosable={!purchaseLoading}
  >
    {purchaseLoading ? (
      <div style={{ textAlign: 'center', padding: '60px 40px' }}>
        <Spin 
          size="large" 
          indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />} 
        />
        <div style={{ marginTop: 30 }}>
          <Title level={4} style={{ color: '#1890ff', marginBottom: 10 }}>
            {selectedPlan?.price === 0 ? 'Activating Free Access...' : 'Processing Payment...'}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 10 }}>
            {selectedPlan?.price === 0 
              ? 'Please wait while we activate your free access...'
              : 'Please wait while we connect to the secure payment gateway...'
            }
          </Text>
          <Progress
            percent={70}
            status="active"
            strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
            style={{ maxWidth: 300, margin: '0 auto' }}
          />
          <Text type="secondary" style={{ fontSize: '12px', color: '#999', marginTop: 10 }}>
            Do not refresh or close this window
          </Text>
        </div>
      </div>
    ) : selectedPlan ? (
      <div style={{ textAlign: 'center' }}>
        {selectedPlan.price === 0 ? (
          <>
            <div style={{ 
              fontSize: '64px', 
              fontWeight: 'bold', 
              color: '#52c41a', 
              margin: '20px 0',
            }}>
              <GiftOutlined />
            </div>
            <Title level={3} style={{ color: '#52c41a', marginBottom: '10px' }}>
              Free Access Available!
            </Title>
            <Text style={{ fontSize: '16px', marginBottom: '30px', color: '#666' }}>
              You can use your one-time free access for this data
            </Text>
            
            <Card style={{ marginBottom: 24, textAlign: 'left' }}>
              <Title level={5} style={{ marginBottom: 16 }}>Free Access Details:</Title>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">Access Type:</Text>
                  <br />
                  <Text strong>Free Lifetime Access</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Duration:</Text>
                  <br />
                  <Text strong>
                    {Math.ceil(
                      moment(form.getFieldValue('dateRange')?.[1]).diff(
                        moment(form.getFieldValue('dateRange')?.[0]), 'days'
                      )
                    )} days
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Date Range:</Text>
                  <br />
                  <Text strong>
                    {form.getFieldValue('dateRange')?.[0]?.format('DD MMM YYYY')} - {' '}
                    {form.getFieldValue('dateRange')?.[1]?.format('DD MMM YYYY')}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Usage:</Text>
                  <br />
                  <Text strong style={{ color: '#52c41a' }}>
                    One-time only
                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: 4 }}>
                      (per account lifetime)
                    </Text>
                  </Text>
                </Col>
              </Row>
            </Card>
            
            <Alert
              type="warning"
              message="Important Notice"
              description="This free access can only be used once per account. Make sure this is the data you want to unlock for free."
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <Button
                type="default"
                size="large"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPlan(null);
                }}
                style={{ width: '150px' }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<GiftOutlined />}
                onClick={() => handleFreeAccess()}
                loading={loading}
                style={{ 
                  width: '200px', 
                  height: '50px', 
                  fontSize: '18px',
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a'
                }}
              >
                Activate Free Access
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ 
              fontSize: '48px', 
              fontWeight: 'bold', 
              color: '#1890ff', 
              margin: '20px 0',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}>
              ₹{selectedPlan.price.toLocaleString()}
            </div>
            <div style={{ fontSize: '18px', marginBottom: '20px', color: '#666' }}>
              One-time payment for <Text strong>lifetime access</Text>
            </div>
            
            <Card style={{ marginBottom: 24, textAlign: 'left' }}>
              <Title level={5} style={{ marginBottom: 16 }}>Purchase Details:</Title>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">Plan:</Text>
                  <br />
                  <Text strong>{selectedPlan.period_label}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Duration:</Text>
                  <br />
                  <Text strong>
                    {Math.ceil(
                      moment(form.getFieldValue('dateRange')?.[1]).diff(
                        moment(form.getFieldValue('dateRange')?.[0]), 'days'
                      )
                    )} days
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Date Range:</Text>
                  <br />
                  <Text strong>
                    {form.getFieldValue('dateRange')?.[0]?.format('DD MMM YYYY')} - {' '}
                    {form.getFieldValue('dateRange')?.[1]?.format('DD MMM YYYY')}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Price:</Text>
                  <br />
                  <Text strong style={{ color: '#1890ff' }}>
                    ₹{selectedPlan.price.toLocaleString()}
                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: 4 }}>
                      (One-time payment)
                    </Text>
                  </Text>
                </Col>
              </Row>
            </Card>
            
            <Alert
              type="info"
              message="Secure Payment"
              description="You will be redirected to Razorpay's secure payment page to complete the transaction."
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <Button
                type="default"
                size="large"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPlan(null);
                }}
                style={{ width: '150px' }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                onClick={() => handleUnlockData(selectedPlan)}
                loading={purchaseLoading}
                style={{ 
                  width: '250px', 
                  height: '50px', 
                  fontSize: '18px',
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff'
                }}
              >
                {purchaseLoading ? 'Processing...' : `Pay ₹${selectedPlan.price.toLocaleString()}`}
              </Button>
            </div>
            
            <div style={{ fontSize: '12px', color: '#999', marginTop: '20px' }}>
              <InfoCircleOutlined /> Powered by Razorpay • 128-bit SSL Security • No card details stored
            </div>
          </>
        )}
      </div>
    ) : null}
  </Modal>
);




const SuccessPaymentModal = () => {
  if (!paymentDetails || paymentStatus !== 'success') return null;
  
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#52c41a' }}>
          <CheckCircleOutlined />
          <span>Payment Successful!</span>
        </div>
      }
      visible={paymentStatus === 'success'}
      onCancel={() => setPaymentStatus(null)}
      footer={[
        <Button 
          key="continue" 
          type="primary" 
          onClick={() => {
            setPaymentStatus(null);
            setPaymentDetails(null);
          }}
        >
          Continue
        </Button>,
        <Button 
          key="view" 
          onClick={() => {
            setPaymentStatus(null);
            setPaymentDetails(null);
            setShowAccessHistory(true);
          }}
        >
          View Access History
        </Button>
      ]}
      width={600}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ 
          fontSize: '64px', 
          color: '#52c41a',
          marginBottom: '16px'
        }}>
          <CheckCircleOutlined />
        </div>
        
        <Title level={3} style={{ color: '#52c41a', marginBottom: '24px' }}>
          Data Access Unlocked!
        </Title>
        
        <Card size="small" style={{ marginBottom: '16px', textAlign: 'left' }}>
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Text type="secondary">Plan:</Text>
              <br />
              <Text strong>{paymentDetails.plan?.period_label}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Amount Paid:</Text>
              <br />
              <Text strong style={{ color: '#1890ff' }}>
                ₹{paymentDetails.amount?.toLocaleString()}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Transaction ID:</Text>
              <br />
              <Text strong style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {paymentDetails.paymentId}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Order ID:</Text>
              <br />
              <Text strong style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {paymentDetails.orderId}
              </Text>
            </Col>
            <Col span={24}>
              <Text type="secondary">Date Range:</Text>
              <br />
              <Text strong>{paymentDetails.dateRange}</Text>
            </Col>
          </Row>
        </Card>
        
        {/* <Alert
          type="success"
          message="Access Activated"
          description="You now have lifetime access to this data. You can download it anytime from the search results."
          showIcon
          style={{ marginTop: '16px' }}
        /> */}
      </div>
    </Modal>
  );
};

const FailurePaymentModal = () => {
  if (!paymentDetails || paymentStatus !== 'failure') return null;
  
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ff4d4f' }}>
          <LockOutlined />
          <span>Payment Failed</span>
        </div>
      }
      visible={paymentStatus === 'failure'}
      onCancel={() => setPaymentStatus(null)}
      footer={[
        <Button 
          key="close" 
          onClick={() => {
            setPaymentStatus(null);
            setPaymentDetails(null);
          }}
        >
          Close
        </Button>,
        <Button 
          key="retry" 
          type="primary" 
          onClick={() => {
            setPaymentStatus(null);
            setPaymentDetails(null);
            setShowPaymentModal(true);
          }}
        >
          Try Again
        </Button>
      ]}
      width={600}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ 
          fontSize: '64px', 
          color: '#ff4d4f',
          marginBottom: '16px'
        }}>
          <LockOutlined />
        </div>
        
        <Title level={3} style={{ color: '#ff4d4f', marginBottom: '16px' }}>
          Payment Failed
        </Title>
        
        <Alert
          type="error"
          message={paymentDetails.error || 'Payment could not be completed'}
          description="Please try again or contact support if the issue persists."
          showIcon
          style={{ marginBottom: '24px' }}
        />
        
        <Card size="small" style={{ textAlign: 'left' }}>
          <Row gutter={[16, 8]}>
            {paymentDetails.plan && (
              <Col span={12}>
                <Text type="secondary">Plan:</Text>
                <br />
                <Text strong>{paymentDetails.plan.period_label}</Text>
              </Col>
            )}
            {paymentDetails.amount && (
              <Col span={12}>
                <Text type="secondary">Amount:</Text>
                <br />
                <Text strong style={{ color: '#1890ff' }}>
                  ₹{paymentDetails.amount?.toLocaleString()}
                </Text>
              </Col>
            )}
            {paymentDetails.orderId && (
              <Col span={24}>
                <Text type="secondary">Order ID:</Text>
                <br />
                <Text strong style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {paymentDetails.orderId}
                </Text>
              </Col>
            )}
          </Row>
        </Card>
        
        {/* <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#fff2e8', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <InfoCircleOutlined style={{ color: '#fa8c16' }} />
            <div>
              <Text strong>Common reasons for payment failure:</Text>
              <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                <li>Insufficient funds in your account</li>
                <li>Card network issues</li>
                <li>Bank server timeout</li>
                <li>Incorrect card details</li>
              </ul>
            </div>
          </div>
        </div> */}
      </div>
    </Modal>
  );
};

  const AccessHistoryModal = () => (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <HistoryOutlined />
          <span>Your Data Access History</span>
        </div>
      }
      visible={showAccessHistory}
      onCancel={() => setShowAccessHistory(false)}
      footer={null}
      width={900}
    >
      <Tabs defaultActiveKey="lifetime">
        {/* <Tabs.TabPane tab="Lifetime Access" key="lifetime" icon={<CrownOutlined />}>
          {lifetimeAccess.length > 0 ? (
            <Table
              dataSource={lifetimeAccess}
              columns={[
                {
                  title: 'HS Code',
                  dataIndex: 'hsCode',
                  key: 'hsCode',
                  render: (text) => text === 'all' ? 'All Codes' : text
                },
                {
                  title: 'Date Range',
                  key: 'dateRange',
                  render: (record) => (
                    <div>
                      {moment(record.dateRangeStart).format('MMM D, YYYY')}
                      <br />
                      to
                      <br />
                      {moment(record.dateRangeEnd).format('MMM D, YYYY')}
                    </div>
                  )
                },
                {
                  title: 'Price',
                  dataIndex: 'price',
                  key: 'price',
                  render: (price) => (
                    <Tag color="purple" icon={<DollarOutlined />}>
                      ₹{price?.toLocaleString() || '0'}
                    </Tag>
                  )
                },
                {
                  title: 'Plan',
                  key: 'plan',
                  render: (record) => {
                    const plan = pricingPlans.find(p => p.id === record.plan_id);
                    return plan ? plan.period_label : 'Custom Plan';
                  }
                },
                {
                  title: 'Purchased On',
                  dataIndex: 'unlockedAt',
                  key: 'unlockedAt',
                  render: (date) => moment(date).format('MMM D, YYYY')
                },
                {
                  title: 'Status',
                  key: 'status',
                  render: () => (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                      Active
                    </Tag>
                  )
                }
              ]}
              size="small"
              pagination={false}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <DatabaseOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <p>No lifetime access purchased yet</p>
              <Button type="primary" onClick={() => setShowPricingPlans(true)}>
                View Plans
              </Button>
            </div>
          )}
        </Tabs.TabPane> */}
        
        <Tabs.TabPane tab="All Access History" key="all" icon={<HistoryOutlined />}>
          {userAccessHistory.length > 0 ? (
            <Table
              dataSource={userAccessHistory}
              columns={[
                {
                  title: 'Type',
                  dataIndex: 'accessType',
                  key: 'accessType',
                  render: (type) => (
                    <Tag color={type === 'free' ? 'green' : 'blue'} icon={type === 'free' ? <GiftOutlined /> : <CrownOutlined />}>
                      {type.toUpperCase()}
                    </Tag>
                  )
                },
                {
                  title: 'HS Code',
                  dataIndex: 'hsCode',
                  key: 'hsCode',
                  render: (text) => text === 'all' ? 'All Codes' : text
                },
                {
                  title: 'Date Range',
                  key: 'dateRange',
                  render: (record) => (
                    <div>
                      {moment(record.dateRangeStart).format('MMM D, YYYY')}
                      <br />
                      to
                      <br />
                      {moment(record.dateRangeEnd).format('MMM D, YYYY')}
                    </div>
                  )
                },
                {
                  title: 'Price',
                  dataIndex: 'price',
                  key: 'price',
                  render: (price) => price ? (
                    <Tag color="purple">₹{price.toLocaleString()}</Tag>
                  ) : (
                    <Tag color="green">FREE</Tag>
                  )
                },
                {
                  title: 'Purchased On',
                  dataIndex: 'unlockedAt',
                  key: 'unlockedAt',
                  render: (date) => moment(date).format('MMM D, YYYY')
                }
              ]}
              size="small"
              pagination={false}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <HistoryOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <p>No access history found</p>
            </div>
          )}
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  );

  const PricingPlansModal = () => (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DollarOutlined />
          <span>Data Access Pricing Plans</span>
        </div>
      }
      visible={showPricingPlans}
      onCancel={() => setShowPricingPlans(false)}
      footer={null}
      width={1000}
    >
      <Alert
        message="Choose the right plan for your needs"
        description="All paid plans include lifetime access to the purchased data range"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      <Row gutter={[24, 24]}>
        {pricingPlans.map((plan) => (
          <Col xs={24} sm={12} md={8} key={plan.id}>
            <PricingCard 
              plan={plan}
              isSelected={selectedPlan?.id === plan.id}
              onSelect={(plan) => {
                setSelectedPlan(plan);
                setShowPaymentModal(true);
                setShowPricingPlans(false);
              }}
            />
          </Col>
        ))}
      </Row>
      
      <div style={{ marginTop: 24, padding: '16px', backgroundColor: '#f6ffed', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px', marginTop: '2px' }} />
          <div>
            <Text strong>What you get with every plan:</Text>
            <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
              <li>Lifetime access to the purchased data range</li>
              <li>Unlimited searches and downloads</li>
              <li>No recurring fees - one time payment only</li>
              <li>Data export in Excel format</li>
              <li>Priority customer support</li>
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );

  const DateInfoModal = () => (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarOutlined />
          <span>Data Availability Information</span>
        </div>
      }
      visible={showDateInfo}
      onCancel={() => setShowDateInfo(false)}
      footer={null}
      width={600}
    >
      <Alert
        message="Important Notice"
        description="Data availability dates and restrictions"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <DatabaseOutlined style={{ color: '#1890ff' }} />
          <Text strong>Data Availability Period</Text>
        </div>
        <Text type="secondary">
          Currently, market data is available from <Text strong>March 2024 to October 2025</Text>.
          Please select date ranges within this period.
        </Text>
      </Card>
      
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <GiftOutlined style={{ color: '#52c41a' }} />
          <Text strong>Free Access Policy</Text>
        </div>
        <Text type="secondary">
          • Free access is limited to <Text strong>7 days</Text> of data<br />
          • Free access can be used <Text strong>only once</Text> per account (lifetime)<br />
          • Unlimited searches and downloads during free access period
        </Text>
      </Card>
      
      <Card size="small">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <CrownOutlined style={{ color: '#722ed1' }} />
          <Text strong>Paid Access Benefits</Text>
        </div>
        <Text type="secondary">
          • All paid plans include <Text strong>lifetime access</Text><br />
          • <Text strong>Unlimited searches and downloads</Text><br />
          • Access to historical data beyond 7 days<br />
          • One-time payment, no recurring fees<br />
          • Priority support and updates
        </Text>
      </Card>
    </Modal>
  );

  const summaryColumns = [
    {
      title: 'HS Code',
      dataIndex: 'hsCode',
      key: 'hsCode',
      responsive: ['md'],
      width: 120,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Loading Port',
      dataIndex: 'loadingPort',
      key: 'loadingPort',
      responsive: ['md'],
      width: 150,
    },
    {
      title: 'Destination',
      children: [
        {
          title: 'Port',
          dataIndex: 'destinationPort',
          key: 'destinationPort',
          width: 150,
        },
        {
          title: 'Country',
          dataIndex: 'destinationCountry',
          key: 'destinationCountry',
          width: 150,
        },
      ],
    },
    {
    title: 'Date Range',
    key: 'dateRange',
    width: 150,
    render: (_, record) => {
      // Get date from form values or from record
      const dateRange = record.formValues?.dateRange || record.dateRange;
      
      if (Array.isArray(dateRange) && dateRange.length === 2) {
        return (
          <div style={{ fontSize: '12px' }}>
            {moment(dateRange[0]).format('MMM D, YY')}
            <br />
            to
            <br />
            {moment(dateRange[1]).format('MMM D, YY')}
          </div>
        );
      } else if (record.dateRange) {
        // If dateRange is a string like "2024-10-25 to 2024-10-31"
        const dates = record.dateRange.split(' to ');
        if (dates.length === 2) {
          return (
            <div style={{ fontSize: '12px' }}>
              {moment(dates[0]).format('MMM D, YY')}
              <br />
              to
              <br />
              {moment(dates[1]).format('MMM D, YY')}
            </div>
          );
        }
      }
      
      return <Text type="secondary">N/A</Text>;
    }
  },
     {
    title: 'Records',
    dataIndex: 'recordCount',
    key: 'recordCount',
    responsive: ['md'],
    width: 120,
    render: (count) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* <Badge 
          count={count} 
          style={{ 
            backgroundColor: count > 0 ? '#1890ff' : '#d9d9d9',
            marginRight: 8
          }}
          showZero
        /> */}
        <Text strong style={{ fontSize: '14px' }}>
          {count?.toLocaleString() || 0}
        </Text>
      </div>
    ),
  },
    {
    title: 'Price',
    key: 'price',
    responsive: ['md'],
    width: 120,
    render: (_, record) => {
      const hasAccess = record.accessInfo?.hasAccess;
      const isFreeAccess = record.accessInfo?.isFreeAccess;
      const priceAmount = record.accessInfo?.priceAmount || priceInfo?.price || 0;
      const purchasedPrice = record.accessInfo?.accessRecord?.price;
      
      if (hasAccess) {
        if (isFreeAccess) {
          return <Tag color="green" icon={<GiftOutlined />}>FREE</Tag>;
        } else if (purchasedPrice) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tag color="purple" icon={<CheckCircleOutlined />}>
                ₹{purchasedPrice.toLocaleString()}
              </Tag>
            </div>
          );
        } else {
          return <Tag color="blue">PAID</Tag>;
        }
      } else {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <LockOutlined style={{ color: '#ff4d4f' }} />
            <Text strong style={{ color: '#ff4d4f' }}>
              ₹{priceAmount ? priceAmount.toLocaleString() : '--'}
            </Text>
          </div>
        );
      }
    },
  },
    {
      title: 'Action',
      key: 'action',
      responsive: ['md'],
      width: 200,
      render: (_, record) => {
        const hasAccess = record.accessInfo?.hasAccess;
        const priceAmount = record.accessInfo?.priceAmount || 
                          record.accessInfo?.requiredPlan?.price;
        
        return (
          <Space>
            <Tooltip title={hasAccess ? "Download Excel file" : "Unlock to download"}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadSpecific(record)}
                size="small"
                loading={downloadingExcel}
                disabled={!hasAccess}
              >
                Download
              </Button>
            </Tooltip>
            
            {!hasAccess && (
              <Tooltip title={`Purchase access for ₹${priceAmount || '--'}`}>
                <Button
                  type="default"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => {
                    const daysSelected = Math.ceil(
                      moment(record.formValues?.dateRange?.[1]).diff(
                        moment(record.formValues?.dateRange?.[0]), 'days'
                      )
                    );
                    const requiredPlan = pricingPlans.find(p => p.period_days >= daysSelected) || 
                                       pricingPlans.find(p => p.price > 0) || 
                                       pricingPlans[pricingPlans.length - 1];
                    setSelectedPlan(requiredPlan);
                    setShowPaymentModal(true);
                  }}
                  size="small"
                >
                  {priceAmount ? `₹${priceAmount}` : 'Unlock'}
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];



 // Add to your Exporterdata component
const [myDownloads, setMyDownloads] = useState([]);
const [showMyDownloads, setShowMyDownloads] = useState(false);
const [downloadsLoading, setDownloadsLoading] = useState(false);

// Fetch My Downloads
const fetchMyDownloads = async () => {
  try {
    setDownloadsLoading(true);
    const response = await axiosInstance.get('/exim-data/my-downloads');
    setMyDownloads(response.data.data || []);
  } catch (error) {
    console.error('Error fetching downloads:', error);
    notification.error({
      message: 'Error',
      description: 'Failed to load your downloads',
    });
  } finally {
    setDownloadsLoading(false);
  }
};

// Download from My Downloads
const handleDownloadFromMyDownloads = async (downloadItem) => {
  try {
    setDownloadingExcel(true);
    
    const response = await axiosInstance.post(
      '/exim-data/download-excel',
      {
        filters: downloadItem.filters,
        fromMyDownloads: true,
        accessId: downloadItem.id
      },
      {
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Exim_Data_${downloadItem.hsCode}_${moment().format('YYYYMMDD')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    notification.success({
      message: 'Download Started',
      description: `Data downloaded successfully`,
    });
    
  } catch (error) {
    console.error('Error downloading:', error);
    notification.error({
      message: 'Download Failed',
      description: 'Failed to download. Please try again.',
    });
  } finally {
    setDownloadingExcel(false);
  }
};

// Search from My Downloads
const handleSearchFromDownload = (downloadItem) => {
  // Pre-fill search form
  form.setFieldsValue({
    hsCode: hsCodeData?.find(hs => hs.code === downloadItem.hsCode)?.id,
    sourceCountry: downloadItem.sourceCountry || 'India',
    sourcePort: downloadItem.sourcePort || 'all',
    destinationCountry: downloadItem.destinationCountry || 'all',
    destinationPort: downloadItem.destinationPort || 'all',
    dateRange: [
      moment(downloadItem.dateRangeStart),
      moment(downloadItem.dateRangeEnd)
    ]
  });
  
  setSelectedDestinationCountry(downloadItem.destinationCountry || 'all');
  setShowMyDownloads(false);
  
  // Trigger search
  handleSearch(form.getFieldsValue());
};

// My Downloads Modal Component
const MyDownloadsModal = () => (
  <Modal
    title={
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CloudDownloadOutlined />
        <span>My Downloads</span>
        {myDownloads.length > 0 && (
          <Badge count={myDownloads.length} style={{ backgroundColor: '#52c41a' }} />
        )}
      </div>
    }
    visible={showMyDownloads}
    onCancel={() => setShowMyDownloads(false)}
    footer={null}
    width={800}
    destroyOnClose
  >
    {downloadsLoading ? (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin indicator={roundLoader} tip="Loading your downloads..." />
      </div>
    ) : myDownloads.length > 0 ? (
      <div>
        <Alert
          message="All your accessed data"
          description="Download anytime or search similar data"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {isMobile ? (
          // Mobile view
          <List
            dataSource={myDownloads}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    key="download"
                    type="primary"
                    icon={<DownloadOutlined />}
                    size="small"
                    onClick={() => handleDownloadFromMyDownloads(item)}
                    loading={downloadingExcel}
                  >
                    Download
                  </Button>,
                  <Button
                    key="search"
                    type="default"
                    icon={<SearchOutlined />}
                    size="small"
                    onClick={() => handleSearchFromDownload(item)}
                  >
                    Search
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      icon={item.accessType === 'free' ? <GiftOutlined /> : <CrownOutlined />} 
                      style={{ 
                        backgroundColor: item.accessType === 'free' ? '#52c41a' : '#722ed1' 
                      }}
                    />
                  }
                  title={
                    <div>
                      <Text strong>{item.title}</Text>
                      {item.accessType === 'free' ? (
                        <Tag color="green" style={{ marginLeft: 8 }}>FREE</Tag>
                      ) : (
                        <Tag color="purple" style={{ marginLeft: 8 }}>
                          ₹{item.price?.toLocaleString()}
                        </Tag>
                      )}
                    </div>
                  }
                  description={
                    <div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        {item.description}
                      </div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                        {moment(item.dateRangeStart).format('MMM D, YY')} - {moment(item.dateRangeEnd).format('MMM D, YY')}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        Unlocked: {moment(item.unlockedAt).format('MMM D, YYYY')}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          // Desktop view
          <Table
            dataSource={myDownloads}
            columns={[
              {
                title: 'Data Details',
                key: 'details',
                render: (record) => (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <Avatar 
                      icon={record.accessType === 'free' ? <GiftOutlined /> : <CrownOutlined />} 
                      style={{ 
                        backgroundColor: record.accessType === 'free' ? '#52c41a' : '#722ed1' 
                      }}
                    />
                    <div>
                      <div>
                        <Text strong>{record.title}</Text>
                        {record.accessType === 'free' ? (
                          <Tag color="green" style={{ marginLeft: 8 }}>FREE</Tag>
                        ) : (
                          <Tag color="purple" style={{ marginLeft: 8 }}>
                            ₹{record.price?.toLocaleString()}
                          </Tag>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        {record.description}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                        Valid: {moment(record.dateRangeStart).format('MMM D, YY')} - {moment(record.dateRangeEnd).format('MMM D, YY')}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                title: 'Action',
                key: 'action',
                width: 200,
                render: (record) => (
                  <Space>
                    <Tooltip title="Download data">
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        size="small"
                        onClick={() => handleDownloadFromMyDownloads(record)}
                        loading={downloadingExcel}
                      >
                        Download
                      </Button>
                    </Tooltip>
                    <Tooltip title="Search similar data">
                      <Button
                        type="default"
                        icon={<SearchOutlined />}
                        size="small"
                        onClick={() => handleSearchFromDownload(record)}
                      >
                        Search
                      </Button>
                    </Tooltip>
                  </Space>
                ),
              },
            ]}
            size="middle"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} accessed datasets`,
            }}
            rowKey="id"
          />
        )}
      </div>
    ) : (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <CloudDownloadOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
        <Title level={4} style={{ color: '#888' }}>No Downloads Yet</Title>
        <p style={{ color: '#888', marginBottom: '24px' }}>
          Your accessed data will appear here after you unlock or purchase data
        </p>
        <Button type="primary" onClick={() => setShowMyDownloads(false)}>
          Start Searching
        </Button>
      </div>
    )}
  </Modal>
);

  const renderMobileSummary = useCallback(
    (data) =>
      data.map((item, index) => {
        const hasAccess = item.accessInfo?.hasAccess;
        const isFreeAccess = item.accessInfo?.isFreeAccess;
        const priceAmount = item.accessInfo?.priceAmount || 
                          item.accessInfo?.requiredPlan?.price;
        const purchasedPrice = item.accessInfo?.accessRecord?.price;
        
        return (
          <Card
            key={index}
            size="small"
            style={{ 
              marginBottom: 16, 
              border: '1px solid #d9d9d9',
              borderLeft: `4px solid ${hasAccess ? '#52c41a' : '#ff4d4f'}`,
              backgroundColor: !hasAccess && priceAmount ? '#fff7e6' : 'white'
            }}
          >
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <Text strong>{item.hsCode || 'N/A'}</Text>
                  </div>
                  <div>
                    {hasAccess ? (
                      isFreeAccess ? (
                        <Tag color="green" size="small">FREE</Tag>
                      ) : (
                        <Tag color="purple" size="small">
                          {purchasedPrice ? `₹${purchasedPrice}` : 'PAID'}
                        </Tag>
                      )
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <LockOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />
                        <Text strong style={{ color: '#ff4d4f', fontSize: '12px' }}>
                          ₹{priceAmount || '--'}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              
              <Col span={24}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <div><strong>From:</strong> {item.loadingPort}</div>
                  <div><strong>To:</strong> {item.destinationPort}, {item.destinationCountry}</div>
                  <div><strong>Records:</strong> <Badge count={item.recordCount} style={{ backgroundColor: '#1890ff' }} /></div>
                </div>
              </Col>
              
              <Col span={24}>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadSpecific(item)}
                    size="small"
                    loading={downloadingExcel}
                    disabled={!hasAccess}
                    block
                  >
                    Download
                  </Button>
                  
                  {!hasAccess && (
                    <Button
                      type="default"
                      icon={<ShoppingCartOutlined />}
                      onClick={() => {
                        const daysSelected = Math.ceil(
                          moment(item.formValues?.dateRange?.[1]).diff(
                            moment(item.formValues?.dateRange?.[0]), 'days'
                          )
                        );
                        const requiredPlan = pricingPlans.find(p => p.period_days >= daysSelected) || 
                                           pricingPlans.find(p => p.price > 0) || 
                                           pricingPlans[pricingPlans.length - 1];
                        setSelectedPlan(requiredPlan);
                        setShowPaymentModal(true);
                      }}
                      size="small"
                      block
                    >
                      {priceAmount ? `₹${priceAmount}` : 'Unlock'}
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        );
      }),
    [downloadingExcel, pricingPlans]
  );

  const totalRecords = summaryData.reduce((total, item) => total + item.recordCount, 0);

  return (
    <div>
      <Card className="myexim-detail-card" style={{ marginBottom: 24 }}>
        <div className="myexim-table-title" style={{ marginBottom: 16 }}>
          {"Export-Import Data Explorer"}
        </div>
        
        <div style={{ marginBottom: 24 }}>
          <Alert
            message={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <DatabaseOutlined /> <strong>Data Availability:</strong> March 2024 to October 2025
                </div>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<InfoCircleOutlined />}
                  onClick={() => setShowDateInfo(true)}
                >
                  More Info
                </Button>
              </div>
            }
            type="info"
            showIcon={false}
            style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}
          />
        </div>

        {/* <AccessStatus /> */}

        <Form form={form} onFinish={handleSearch} className="gx-pt-4">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={12}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent(
                  "Country Of Origin",
                  "Currently we are allowing Loading country as India only"
                )}
                name="sourceCountry"
                rules={[
                  {
                    required: !isExporter(authUser),
                    message: "Loading Country is required",
                  },
                ]}
              >
                <Select
                  defaultValue={isExporter(authUser) ? "India" : null}
                  disabled={isEditDisabled || isExporter(authUser)}
                  showSearch
                  size="large"
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={selectedSourceCountry}
                  placeholder="Select Loading Country"
                >
                  <Option key="all" value="all">All Countries</Option>
                  {countries?.map(
                    (option) =>
                      option.country !== "" && (
                        <Option key={option.country} value={option.country}>
                          {option.country}
                        </Option>
                      )
                  )}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label="Date Range"
                name="dateRange"
                rules={[
                  {
                    required: true,
                    message: "Date range is required",
                  },
                ]}
              >
                <RangePicker 
                  style={{ width: '100%' }} 
                  size="large"
                  format="YYYY-MM-DD"
                  disabledDate={(current) => {
                    if (current && current > moment('2025-10-31').endOf('day')) {
                      return true;
                    }
                    
                    if (current && current < moment('2024-03-01').startOf('day')) {
                      return true;
                    }
                    
                    return false;
                  }}
                  ranges={{
                    'Last 7 Days (Free)': [
                      moment().subtract(7, 'days'),
                      moment()
                    ],
                    'Last 30 Days': [
                      moment().subtract(30, 'days'),
                      moment()
                    ],
                    'Last 3 Months': [
                      moment().subtract(90, 'days'),
                      moment()
                    ]
                  }}
                />
              </Form.Item>
            </Col>

         <Col xs={24} sm={12} md={12}>
  <Form.Item
    disabled={isEditDisabled}
    labelCol={{ span: 24 }}
    wrapperCol={{ span: 24 }}
    label={labelComponent("HS Code / Item Description")}
    name="hsCode"
    rules={[
      {
        required: true,
        message: "HS code is required",
      },
    ]}
  >
    <Select
      showSearch
      size="large"
      placeholder="Search HS code (e.g., 1006) or Item Description"
      optionFilterProp="children"
      filterOption={(input, option) => {
        const inputLower = input.toLowerCase();
        // Safely get option text
        const optionText = (option?.children || '').toString().toLowerCase();
        return optionText.includes(inputLower);
      }}
      dropdownStyle={{ maxHeight: "300px", overflowY: "auto" }}
      getPopupContainer={(trigger) => trigger.parentNode}
      disabled={isEditDisabled}
      onChange={(value) => {
        setHsCodeId(value);
      }}
      onSearch={handleHSCodeSearch}
      notFoundContent={
        <div style={{ textAlign: 'center', padding: '10px' }}>
          <p>No HS Code found</p>
          <small style={{ color: '#999' }}>
            Try searching by number (e.g., 1006) or item description
          </small>
        </div>
      }
    >
      {/* This will now only show 1006 items */}
      {hsCodeData?.map((option) => (
        <Option key={option.id} value={option.id}>
          {`${option.code} - ${option.description}`}
        </Option>
      ))}
    </Select>
  </Form.Item>
</Col>

            <Col xs={24} sm={12} md={12}>
              <Form.Item
                disabled={isEditDisabled}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent(
                  "Port Of Origin",
                  "Select the Loading Port where Goods to be loaded"
                )}
                name="sourcePort"
                rules={[
                  {
                    required: true,
                    message: "Loading Port is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  virtual={false}
                  size="large"
                  placeholder="Select Loading Port"
                  disabled={!selectedSourceCountry}
                  filterOption={(input, option) => {
                    const label = option.children;
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  <Option key="all" value="all">All Ports</Option>
                  {indianPorts?.map((option) => (
                    <Option key={option.port} value={option.port}>
                      {`${option.port} (${option.code})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12}>
  <Form.Item
    disabled={isEditDisabled}
    labelCol={{ span: 24 }}
    wrapperCol={{ span: 24 }}
    label={labelComponent(
      "Destination Country",
      "Select the Destination Country where Goods to be Delivered"
    )}
    name="destinationCountry"
    rules={[
      {
        required: true,
        message: "Destination Country is required",
      },
    ]}
  >
    <Select
      showSearch
      size="large"
      placeholder="Select Destination Country"
      onChange={(value) => {
        setSelectedDestinationCountry(value); 
      }}
      value={selectedDestinationCountry}
      getPopupContainer={(trigger) => trigger.parentNode}
      filterOption={(input, option) => {
        if (!option || !option.children) return false;
        return option.children.toLowerCase().includes(input.toLowerCase());
      }}
    >
      <Option key="all" value="all">All Countries</Option>
      {countries
        ?.filter(option => 
          option.country && 
          option.country.trim() !== "" && 
          option.country !== " " && 
          option.country !== undefined
        )
        ?.map((option) => (
          <Option key={option.country} value={option.country}>
            {option.country.trim()}
          </Option>
        ))}
    </Select>
  </Form.Item>
</Col>

            <Col xs={24} sm={12} md={12}>
              <Form.Item
                disabled={isEditDisabled}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent(
                  "Destination Port",
                  "Select the Destination Port where Goods to be Delivered"
                )}
                name="destinationPort"
                rules={[
                  {
                    required: true,
                    message: "Destination Port is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  size="large"
                  placeholder="Select Destination Port"
                  disabled={!selectedDestinationCountry} 
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  <Option key="all" value="all">All Ports</Option>
                  {destinationPortsData.map((option) => (
                    <Option key={option.port} value={option.port}>
                      {`${option.port} (${option.code})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row className="gx-mt-6">
            <Col span={24}>
             <div style={{ textAlign: 'center', padding: '20px 0', backgroundColor: '#fafafa', borderRadius: '8px', marginBottom: '20px' }}>
  <p style={{ color: '#666', fontSize: '16px', marginBottom: '16px' }}>
    {/* <InfoCircleOutlined /> Free data access: 7 days once in lifetime. Choose plan: */}
  </p>
  
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    flexWrap: 'wrap',
    gap: '8px'
  }}>
    {pricingPlans.map((plan) => (
      <Tag
        key={plan.id}
        color={plan.price === 0 ? "success" : "processing"}
        style={{ 
          fontSize: '14px', 
          padding: '8px 16px',
          cursor: 'pointer',
          border: selectedPlan?.id === plan.id ? '2px solid #1890ff' : 'none',
          fontWeight: selectedPlan?.id === plan.id ? 'bold' : 'normal'
        }}
        // onClick={() => {
        //   setSelectedPlan(plan);
        //   setShowPaymentModal(true);
        // }}
      >
        {plan.period_label} - {plan.price === 0 ? 'Free' : `₹${plan.price}`}
      </Tag>
    ))}
  </div>
</div>
              
              <Form.Item style={{ marginTop: 16 }}>
  <Row justify="end" gutter={[16, 16]}>
    {freeAccessUsed && (
      <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'center' : 'right' }}>
        <Tooltip title="You have used your free access. Upgrade for more data.">
          <Button
            type="primary"
            size="large"
            onClick={() => setShowPaymentModal(true)}
            icon={<CrownOutlined />}
            style={{ 
              backgroundColor: '#722ed1', 
              borderColor: '#722ed1',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Upgrade Now
          </Button>
        </Tooltip>
      </Col>
    )}
    
    <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'center' : 'right' }}>

     <Button
      type="default"
      size="large"
      icon={<HistoryOutlined />}
      onClick={() => setShowAccessHistory(true)}
    >
      Access History
    </Button>
      <Button
        type="primary"
        size="large"
        htmlType="submit"
        style={{ 
          width: isMobile ? '100%' : 150,
          minWidth: 150
        }}
        loading={loading}
        // icon={<EyeOutlined />}
      >
        Search Data
      </Button>

       {/* <Button
          type="default"
          size="large"
          icon={<CloudDownloadOutlined />}
          onClick={() => setShowMyDownloads(true)}
        >
          My Downloads
        </Button> */}

      
    </Col>
  </Row>
</Form.Item>
            </Col>
          </Row>
        </Form>

        {hasSearched && totalRecords > 0 && (  // Add totalRecords > 0 condition
  <div style={{ marginTop: 24 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '16px' }}>
      <div className="myexim-table-title" style={{ marginBottom: 0 }}>
        {"Data Summary"}
        <Text type="secondary" style={{ marginLeft: '12px', fontSize: '14px' }}>
          ({totalRecords} records found)
        </Text>
      </div>
      
      {accessInfo?.hasAccess && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={() => handleDownloadSpecific(summaryData[0])}
            loading={downloadingExcel}
            disabled={!accessInfo.hasAccess}
          >
            Download All ({totalRecords} records)
          </Button>
          
          <Tooltip title="Refresh search results">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => handleSearch(form.getFieldsValue())}
            />
          </Tooltip>
        </div>
      )}
    </div>
    
    {/* AccessStatus should only show when there's data to unlock */}
   
    
    {summaryLoading ? (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin indicator={roundLoader} tip="Loading summary data..." size="large" />
      </div>
    ) : summaryData.length > 0 ? (
      isMobile ? (
        <div className="mobile-summary-list">
          {renderMobileSummary(summaryData)}
        </div>
      ) : (
        <Table
          columns={summaryColumns}
          dataSource={summaryData}
          rowKey={(record, index) => `${record.hsCode}-${record.loadingPort}-${record.destinationPort}-${index}`}
          pagination={summaryData.length > 10 ? {
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} items`
          } : false}
          scroll={{ x: true }}
          size="middle"
          style={{ marginTop: 16 }}
        />
      )
    ) : null}

     {!accessInfo?.hasAccess && (
      <AccessStatus />
    )}
  </div>
)}

{/* Show message when no records found */}
{hasSearched && totalRecords === 0 && !summaryLoading && (
  <Card style={{ marginTop: 24, textAlign: "center", padding: "40px" }}>
    <DatabaseOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
    <Title level={4} style={{ color: '#888' }}>No Data Found</Title>
    <p style={{ color: '#888', marginBottom: '24px' }}>
      No records found for the selected criteria. Please try with different search parameters.
    </p>
    <Button type="primary" onClick={() => form.resetFields()}>
      Try Different Search
    </Button>
  </Card>
)}
      </Card>

     <PaymentModal />
    <SuccessPaymentModal />
    <FailurePaymentModal />
    <AccessHistoryModal />
    <PricingPlansModal />
    <DateInfoModal />
    <MyDownloadsModal />
    <MyDownloadsTable />


    </div>
  );
};

export default Exporterdata;